import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Pagination from '../../components/Pagination';
import {
  FaExclamationTriangle,
  FaChartBar,
  FaBullhorn,
  FaSearch,
  FaBell
} from 'react-icons/fa';
import './FarmerAlerts.css';

const PER_PAGE = 10;

const AlertTypeIcon = ({ type }) => {
  const cls = 'farmer-alerts-type-icon-svg';
  switch (type) {
    case 'low-stock':
      return <FaExclamationTriangle className={cls} aria-hidden />;
    case 'performance':
      return <FaChartBar className={cls} aria-hidden />;
    case 'admin':
      return <FaBullhorn className={cls} aria-hidden />;
    case 'quality':
      return <FaSearch className={cls} aria-hidden />;
    default:
      return <FaBell className={cls} aria-hidden />;
  }
};

const FarmerAlerts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);

  const [alerts] = useState([
    {
      id: 1,
      type: 'low-stock',
      priority: 'high',
      title: t('farmerAlerts.lowStockAlert'),
      message: 'Your product "Sweet Potatoes" is running low. Only 5kg remaining.',
      product: 'Sweet Potatoes',
      stock: 5,
      threshold: 10,
      timestamp: new Date('2024-01-20T10:30:00'),
      action: 'restock'
    },
    {
      id: 2,
      type: 'performance',
      priority: 'medium',
      title: t('farmerAlerts.weeklyPerformance'),
      message: 'Your sales increased by 15% this week! Great job!',
      metric: '+15%',
      timestamp: new Date('2024-01-19T08:00:00'),
      action: 'view'
    },
    {
      id: 3,
      type: 'admin',
      priority: 'medium',
      title: t('farmerAlerts.adminMessage'),
      message: 'Your verification documents have been approved.',
      timestamp: new Date('2024-01-18T14:20:00'),
      action: 'view'
    },
    {
      id: 4,
      type: 'quality',
      priority: 'high',
      title: t('farmerAlerts.qualityWarning'),
      message: 'A customer reported an issue with product quality. Please review.',
      product: 'Fresh Tomatoes',
      timestamp: new Date('2024-01-17T16:45:00'),
      action: 'review'
    },
    {
      id: 5,
      type: 'low-stock',
      priority: 'medium',
      title: t('farmerAlerts.stockAlert'),
      message: 'Your product "Fresh Carrots" is running low. Only 15kg remaining.',
      product: 'Fresh Carrots',
      stock: 15,
      threshold: 20,
      timestamp: new Date('2024-01-16T09:15:00'),
      action: 'restock'
    },
    {
      id: 6,
      type: 'admin',
      priority: 'low',
      title: t('farmerAlerts.productApproved'),
      message: 'Your new product "Cauliflower" has been approved and is now live.',
      product: 'Cauliflower',
      timestamp: new Date('2024-01-15T11:30:00'),
      action: 'view'
    }
  ]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== 'farmer') {
          navigate('/');
        }
        setLoading(false);
      } catch (error) {
        console.error('Error parsing user data:', error);
        setLoading(false);
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#F44336';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#9E9E9E';
    }
  };

  useEffect(() => { setPage(1); }, [filter]);

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.type === filter;
  });

  const totalPages = Math.ceil(filteredAlerts.length / PER_PAGE);
  const pageAlerts = filteredAlerts.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const getStockPercentage = (stock, threshold) => {
    return Math.min((stock / threshold) * 100, 100);
  };

  if (loading) {
    return (
      <div className="farmer-alerts-page">
        <Header />
        <div className="farmer-alerts-loading-state">
          <div className="farmer-alerts-spinner"></div>
          <p>{t('farmerAlerts.loading')}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="farmer-alerts-page">
      <Header />
      
      <div className="farmer-alerts-container">
        <div className="farmer-alerts-header">
          <div>
            <h1 className="farmer-alerts-title">{t('farmerAlerts.title')}</h1>
            <p className="farmer-alerts-subtitle">{t('farmerAlerts.subtitle')}</p>
          </div>
          <div className="farmer-alerts-stats">
            <div className="farmer-alerts-stat-badge high">{t('farmerAlerts.high', { count: alerts.filter(a => a.priority === 'high').length })}</div>
            <div className="farmer-alerts-stat-badge medium">{t('farmerAlerts.medium', { count: alerts.filter(a => a.priority === 'medium').length })}</div>
            <div className="farmer-alerts-stat-badge low">{t('farmerAlerts.low', { count: alerts.filter(a => a.priority === 'low').length })}</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="farmer-alerts-filters">
          <button
            className={`farmer-alerts-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({alerts.length})
          </button>
          <button
            className={`farmer-alerts-filter-btn ${filter === 'low-stock' ? 'active' : ''}`}
            onClick={() => setFilter('low-stock')}
          >
            Low Stock ({alerts.filter(a => a.type === 'low-stock').length})
          </button>
          <button
            className={`farmer-alerts-filter-btn ${filter === 'performance' ? 'active' : ''}`}
            onClick={() => setFilter('performance')}
          >
            Performance ({alerts.filter(a => a.type === 'performance').length})
          </button>
          <button
            className={`farmer-alerts-filter-btn ${filter === 'admin' ? 'active' : ''}`}
            onClick={() => setFilter('admin')}
          >
            Admin ({alerts.filter(a => a.type === 'admin').length})
          </button>
          <button
            className={`farmer-alerts-filter-btn ${filter === 'quality' ? 'active' : ''}`}
            onClick={() => setFilter('quality')}
          >
            Quality ({alerts.filter(a => a.type === 'quality').length})
          </button>
        </div>

        {/* Alerts List */}
        <div className="farmer-alerts-list">
          {filteredAlerts.length === 0 ? (
            <div className="farmer-alerts-empty-alerts">
              <div className="farmer-alerts-empty-icon" aria-hidden>
                <FaBell />
              </div>
              <h3>{t('farmerAlerts.noAlerts')}</h3>
              <p>{t('farmerAlerts.allCaughtUp')}</p>
            </div>
          ) : (
            pageAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`farmer-alerts-alert-card priority-${alert.priority}`}
                style={{ borderLeftColor: getPriorityColor(alert.priority) }}
              >
                <div className="farmer-alerts-alert-icon">
                  <AlertTypeIcon type={alert.type} />
                </div>
                <div className="farmer-alerts-alert-content">
                  <div className="farmer-alerts-alert-header">
                    <h3 className="farmer-alerts-alert-title">{alert.title}</h3>
                    <span
                      className="farmer-alerts-priority-badge"
                      style={{ backgroundColor: getPriorityColor(alert.priority) }}
                    >
                      {alert.priority.toUpperCase()}
                    </span>
                  </div>
                  <p className="farmer-alerts-alert-message">{alert.message}</p>
                  
                  {alert.type === 'low-stock' && alert.stock !== undefined && (
                    <div className="farmer-alerts-stock-meter-section">
                      <div className="farmer-alerts-stock-meter-label">
                        <span>Current Stock: {alert.stock}kg</span>
                        <span>Threshold: {alert.threshold}kg</span>
                      </div>
                      <div className="farmer-alerts-stock-meter-bar">
                        <div
                          className="farmer-alerts-stock-meter-fill"
                          style={{
                            width: `${getStockPercentage(alert.stock, alert.threshold)}%`,
                            backgroundColor: getStockPercentage(alert.stock, alert.threshold) < 30 
                              ? '#F44336' 
                              : getStockPercentage(alert.stock, alert.threshold) < 60 
                              ? '#FF9800' 
                              : '#4CAF50'
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {alert.type === 'performance' && alert.metric && (
                    <div className="farmer-alerts-performance-metric">
                      <span className="farmer-alerts-metric-value">{alert.metric}</span>
                      <span className="farmer-alerts-metric-label">Sales Increase</span>
                    </div>
                  )}

                  <div className="farmer-alerts-alert-footer">
                    <span className="farmer-alerts-alert-time">
                      {new Date(alert.timestamp).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <button
                      className={`farmer-alerts-action-btn action-${alert.action}`}
                      onClick={() => {
                        if (alert.action === 'restock') {
                          navigate(`/farmer/products/edit/${alert.product}`);
                        } else if (alert.action === 'view') {
                          navigate('/farmer/dashboard');
                        } else if (alert.action === 'review') {
                          navigate('/farmer/orders');
                        }
                      }}
                    >
                      {alert.action === 'restock' ? t('farmerAlerts.restockNow') :
                       alert.action === 'view' ? t('farmerAlerts.viewDetails') :
                       t('farmerAlerts.reviewIssue')}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      <Footer />
    </div>
  );
};

export default FarmerAlerts;

